import toast from 'react-hot-toast';

// One place for every upload in the admin panel: the limits that the server enforces, the note shown next to each upload
// button, the check done BEFORE sending, and the message shown when the server (or the web server in front of it) refuses.
// Keep the numbers in step with the multer limits in server/routes.
export const RULES = {
    photo: { maxMB: 5, kinds: 'image', what: 'photo' },                 // banners, blog cover, service cards, provider logo / gallery, profile photo / gallery
    document: { maxMB: 8, kinds: 'image-pdf', what: 'document' },       // Aadhaar and horoscope files
    story: { maxMB: 15, kinds: 'image', what: 'photo' },                // success-story couple photo
    notice: { maxMB: 10, kinds: 'any', what: 'file' },                  // files attached to a notification
};

const KIND_TEXT = { image: 'JPG, PNG or WebP', 'image-pdf': 'JPG, PNG, WebP or PDF', any: 'image or document' };

export const sizeText = bytes => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// "JPG, PNG or WebP · up to 5 MB each · up to 15 photos"
export const limitNote = (rule, { maxFiles } = {}) => [
    KIND_TEXT[rule.kinds],
    `up to ${rule.maxMB} MB${maxFiles && maxFiles > 1 ? ' each' : ''}`,
    maxFiles && maxFiles > 1 ? `up to ${maxFiles} files` : '',
].filter(Boolean).join(' · ');

const typeOk = (file, kinds) => {
    if (kinds === 'any') return true;
    const type = String(file.type || '').toLowerCase();
    if (type.startsWith('image/')) return true;
    return kinds === 'image-pdf' && type === 'application/pdf';
};

// Checks the chosen files before anything is sent. Shows ONE toast that names every problem, and returns the files that are fine.
export const checkFiles = (input, rule, { have = 0, maxFiles } = {}) => {
    const files = Array.from(input || []).filter(Boolean);
    const problems = [];
    const good = [];
    for (const file of files) {
        if (!typeOk(file, rule.kinds)) problems.push(`${file.name} is not allowed. Choose ${KIND_TEXT[rule.kinds]}.`);
        else if (file.size > rule.maxMB * 1024 * 1024) problems.push(`${file.name} is ${sizeText(file.size)}. The maximum is ${rule.maxMB} MB.`);
        else good.push(file);
    }
    let accepted = good;
    if (maxFiles) {
        const room = Math.max(maxFiles - have, 0);
        if (good.length > room) {
            problems.push(room ? `Only ${room} more ${room === 1 ? 'file' : 'files'} can be added (the limit is ${maxFiles}).` : `The limit of ${maxFiles} files is already reached.`);
            accepted = good.slice(0, room);
        }
    }
    if (problems.length) {
        const shown = problems.slice(0, 3).join(' ');
        toast.error(problems.length > 3 ? `${shown} (and ${problems.length - 3} more problems)` : shown, { duration: 6000 });
    }
    return accepted;
};

// The message for a failed upload request.
export const uploadFailMessage = (error, files, rule) => {
    const list = Array.from(files || []).filter(Boolean);
    const biggest = list.reduce((max, file) => Math.max(max, file.size || 0), 0);
    const status = error?.response?.status;
    // When the web server in front of the API refuses a big file (413 Content Too Large) its answer carries no CORS headers, so the
    // browser hides it and reports a plain network error. A big file while the internet is fine means exactly that.
    const hiddenTooLarge = !error?.response && navigator.onLine !== false && biggest > 1024 * 1024;
    if (status === 413 || hiddenTooLarge) {
        return `413 Content Too Large: ${list.length > 1 ? 'these files are' : 'this file is'} too large for the server${biggest ? ` (largest: ${sizeText(biggest)})` : ''}. The limit is ${rule.maxMB} MB each. Please choose a smaller file.`;
    }
    if (!error?.response) return 'The upload could not reach the server. Please check your internet connection and try again.';
    return error.response.data?.message || `Upload failed. Please try again (maximum ${rule.maxMB} MB each).`;
};
